import React, { useEffect, useState, useCallback, useRef } from 'react'
import { useHistory } from 'react-router'
import classNames from 'classnames'
import { camelize } from 'humps'
import dayjs from 'dayjs'

// UIKIT dependencies
import { CircularLoader as Loader, Input } from '@st-operations/uikit/src/v2/index.js'
import format from '@st-operations/uikit/lib/format.js'

// Hooks
import { useSelectedTopics } from './hooks/index'
import { useRetractSidebar } from '_hooks'
import { useUser, usePrevious } from '_hooks'
import { usePrebuiltTopics } from './hooks/usePrebuiltTopics'

// Helpers and Integration
import {
  getCustomAudienceSchema,
  getCustomAudience,
  updateCustomAudience,
  deleteCustomAudience,
  getTopicsSizes,
  getSizeEstimation,
  getRegionSizeEstimation,
  getTaxonomyAudience,
  updateTaxonomyAudience,
} from '_services/audience'
import { getRegions } from '_services/regions'
import { getDestination, getDestinations } from '_services/destinations'

// Components
import AudienceCreatorHeader, { STATUSES } from './components/header/index.jsx'
import DestinationsFilter from './components/destinations-filter'
import GeneralFilters from './components/general-filters'
import IconACSizes from './components/IconSizes'
import RegionSizes from './components/region-sizes'
import Section from './components/section'
import SeedsBlock from './components/seeds-block'
import SegmentIds from './components/segment-ids'
import ApiIntegrations from './components/api-integrations'
import UserIdSeedBlock from './components/user-id-seeds-block'
import SelectedDestinations from './components/selected-destinations/index.jsx'
import SignalsBlock from './components/signals-block'
import SignalsFilter from './components/signals-filter'

// Misc
import { logErrorAsync } from '_lib/utility'
import asyncDebounce from '_lib/asyncDebounce'
import {
  getDiffKeys,
  getSelectedTopics,
  handleFormatNumber,
  EXCEPTED_REGIONS,
  extractDemographics,
} from './utils'

import './styles.scss'

const EU_GDPR = require('_constants/eu_gdpr.js')

const debouncedGetSizeEstimation = asyncDebounce(getSizeEstimation, 200)
const debouncedGetRegionSizeEstimation = asyncDebounce(getRegionSizeEstimation, 200)

export const AC_PAGE_TYPES = {
  AC: 'audience-creator',
  TX: 'taxonomy',
}

const SIGNALS_OPTIONS = {
  Topics: false,
  'URL Seeds': false,
  'User ID Seeds': false,
}

const getInitialSignals = (audience) => {
  const hasTopics = audience?.topics?.filter((t) => !t.atoms.seeds).length > 0
  const hasSeed = audience?.seed?._id
  const hasUserIdSeed = audience?.userIdSeed?._id
  const isEmpty = !hasTopics && !hasSeed && !hasUserIdSeed

  return {
    Topics: isEmpty || hasTopics,
    'URL Seeds': hasSeed,
    'User ID Seeds': hasUserIdSeed,
  }
}

// NOTE: this same component is used to render taxonomy pages
const AudienceCreator = ({
  match: {
    params: { id },
  },
  pageType = AC_PAGE_TYPES.AC,
}) => {
  useRetractSidebar()
  useUser()
  const history = useHistory()

  const [loading, setLoading] = useState({
    load: false,
    regions: false,
    destinations: false,
    draft: false,
    approving: false,
    delete: false,
    archive: false,
    save: false,
    fetchingTopicsSizes: false,
  })
  const [schema, setSchema] = useState(null)
  const [audience, setAudience] = useState(null)
  const previousAudience = usePrevious(audience)
  const [hasChanges, setHasChanges] = useState(0)
  const [regions, setRegions] = useState([])
  const [destinations, setDestinations] = useState([])
  const [disabledInputAndSelect, setDisabledInputAndSelect] = useState(false)
  const [isEU, setIsEU] = useState(false)
  const [offlineAttributes, setOfflineAttributes] = useState([])
  const [sizes, setSizes] = useState({})
  const [regionSizes, setRegionSizes] = useState(null)
  const regionSizesRequest = useRef(0)
  const [signals, setSignals] = useState(SIGNALS_OPTIONS)

  const getProcessedAttributes = usePrebuiltTopics()

  const disabledAll = !!audience?.resourceOptimizedClone /*|| ['approved', 'processed'].includes(audience?.status)*/

  const nonSeedTopics = (getSelectedTopics(audience) || [])
    .filter((t) => !t?.atoms?.seeds)
    .filter(Boolean)

  const { handleRefreshTopics, setPreBuiltAttrTypes, ...restUseSelectedTopics } = useSelectedTopics(
    {
      audience,
      setAudience,
    }
  )

  // load data
  useEffect(() => {
    async function getData() {
      setLoading((prev) => ({
        ...prev,
        regions: true,
        destinations: true,
        audience: true,
      }))

      try {
        // make requests in parallel
        const [regionsResponse, destinationsResponse, schemaResponse, audienceResponse] =
          await Promise.all([
          getRegions({
            fields: ['_id', 'name', 'countries', 'subdivisions', 'cities'],
            limit: 1000,
          }),
          getDestinations({
            fields: ['_id', 'api_integration', 'name', 'slug'],
            limit: 1000,
          }),
          getCustomAudienceSchema(),
          pageType === AC_PAGE_TYPES.AC
            ? getCustomAudience(id)
            : pageType === AC_PAGE_TYPES.TX
            ? getTaxonomyAudience(id)
            : Promise.resolve(null),
          ])

        // update states
        setRegions(
          regionsResponse.items
            .filter((region) => !EXCEPTED_REGIONS.includes(region.name))
            .sort((a, b) => (a.name > b.name ? 1 : -1))
        )
        setDestinations(destinationsResponse.items.sort((a, b) => (a.name > b.name ? 1 : -1)))
        setSchema(schemaResponse)
        setAudience(audienceResponse)
        setSignals(getInitialSignals(audienceResponse))

        // handle errors
      } catch (error) {
        logErrorAsync(error)
        toast.error(error.message || 'An error occurred')

        // clear loading
      } finally {
        setLoading((prev) => ({
          ...prev,
          regions: false,
          destinations: false,
          audience: false,
        }))
      }
    }

    getData()
  }, [id, pageType])

  // Get destinations size estimations
  useEffect(() => {
    const getSizes = async () => {
      const payload = {
        destinations: audience.destinations,
        geo_audience_size: audience.geoAudienceSize,
        geo_audience: audience?.geoAudience,
        regions: audience.regions
          ?.map((selectedRegion) => regions.find((region) => region._id === selectedRegion._id))
          .filter(Boolean),
        // conditionally include topics
        ...(signals.Topics
          ? {
              topics: audience.topics,
            }
          : {}),
        // Demographics attributes
        languages: audience.languages,
        ...extractDemographics(audience),
        // --
        filters: [
          {
            key: 'audience',
            operator: 'equals',
            value: audience['_id'],
          },
        ],
        // conditionally include retail preferences
        ...(audience.retailerPreference?.length
          ? { retailerPreference: audience.retailerPreference }
          : {}),
        afs_brands: [
          ...(audience?.afsBrand || []),
          ...(audience?.afsCategory || []),
          ...(audience?.afsLifestyle || []),
          ...(audience?.afsLiveramp || []),
        ],
        customAttributes: audience?.customAttributes ?? [],
        predictors: audience?.predictors ?? [],
      }

      getRegionSizes(payload)

      try {
        const response = await debouncedGetSizeEstimation(payload)
        setSizes(response)
      } catch (error) {
        logErrorAsync(error)
        toast.error(error.message)
      }
    }

    // size each selected region on its own for the region breakdown
    const getRegionSizes = async (payload) => {
      const request = ++regionSizesRequest.current
      if (payload.geo_audience || !(payload.regions?.length > 1)) {
        setRegionSizes(null)
        return
      }

      setRegionSizes((previous) => previous ?? {})
      try {
        const response = await debouncedGetRegionSizeEstimation(payload)
        if (request !== regionSizesRequest.current) return
        setRegionSizes(
          Object.fromEntries((response || []).map((region) => [region._id, region.size]))
        )
      } catch (error) {
        logErrorAsync(error)
        if (request === regionSizesRequest.current) setRegionSizes(null)
      }
    }

    const getSeedSizes = async () => {
      const thresholdSizes = audience.seed.thresholds?.[audience.threshold.label]?.sizes
      const mappingPromises = audience.destinations.map((destination) =>
        getDestination(destination._id).then((destinationData) =>
          camelize(destinationData?.mappingId?.name || '')
        )
      )
      const mapping = await Promise.all(mappingPromises)
      const newSizes = audience.destinations.reduce(
        (prev, curr, index) => ({
          ...prev,
          [mapping[index]]: thresholdSizes[mapping[index]],
          [curr._id]: thresholdSizes[mapping[index]],
        }),
        {}
      )
      // if sharethis is not in the mapping, add it
      if (thresholdSizes.sharethis && !newSizes?.sharethis) {
        newSizes.sharethis = thresholdSizes.sharethis
      }
      setSizes(newSizes)
    }

    const selectedTopicsForModal = nonSeedTopics || []
    const filteredTopic = selectedTopicsForModal.filter((topic) => topic?.type)
    const processedAttributes = getProcessedAttributes(filteredTopic)
    setOfflineAttributes(processedAttributes)
    const preBuiltAttrTypes = processedAttributes.map((attr) => attr.attrName)
    setPreBuiltAttrTypes(preBuiltAttrTypes)

    if (audience?._id) {
      if (signals['URL Seeds'] && audience?.seed?._id) {
        regionSizesRequest.current++
        setRegionSizes(null)
        getSeedSizes()
      } else if (signals.Topics) {
        getSizes()
      }
    }
  }, [audience, getProcessedAttributes, signals, regions])

  // handle EU changes
  useEffect(() => {
    if (audience && previousAudience) {
      const diffKeys = getDiffKeys(audience, previousAudience)
      const changedDestinationsOrRegions =
        diffKeys.includes('destinations') || diffKeys.includes('regions')

      const topicsAreEquals =
        JSON.stringify(audience.topics.map((t) => t.atoms.keywords)) ===
        JSON.stringify(previousAudience.topics.map((t) => t.atoms.keywords))

      if (changedDestinationsOrRegions || !topicsAreEquals) {
        const keys = ['destinations', 'regions']
        const newIsEU = keys?.some((key) =>
          audience[key]?.some(
            (item) =>
              item.name.includes('EU') ||
              item.name.includes('Europe') ||
              EU_GDPR.includes(item.countries && item.countries[0])
          )
        )

        if (audience?.topics?.filter((f) => !f.atoms.seeds)?.length > 0) {
          getTopics()
        }

        setIsEU(newIsEU)
      }

      setHasChanges((prev) => prev + 1)
    }
    setDisabledInputAndSelect(
      audience?.kind === 'custom' &&
        (audience?.status === STATUSES.APPROVED || audience?.status === STATUSES.PROCESSED)
    )
  }, [audience])

  useEffect(() => {
    handleRefreshTopics()
  }, [isEU])

  const getTopics = async () => {
    try {
      const response = await getTopicsSizes({
        afs_brands: [
          ...(audience?.afsBrand || []),
          ...(audience?.afsCategory || []),
          ...(audience?.afsLifestyle || []),
          ...(audience?.afsLiveramp || []),
        ],
        destinations: audience.destinations,
        regions: audience.regions,
        topics: audience.topics,
        languages: audience.languages,
        ...extractDemographics(audience),
        custom_attributes: audience?.customAttributes ?? [],
        predictors: audience?.predictors ?? [],
      })

      setAudience((prev) => ({
        ...prev,
        topics: prev.topics.map((topic) => ({ ...topic, sizes: response[topic.name] })),
      }))
    } catch (error) {
      logErrorAsync(error)
      toast.error(error.message)
    }
  }

  // v1 and v2 selects handle data differently
  const handleInputChange = (v2Value, name, v1Value) => {
    setAudience((prevState) => ({
      ...prevState,
      [name]: v1Value ?? v2Value,
    }))
  }

  const handleInputNumberChange = (value, name) => {
    setAudience((prevState) => ({
      ...prevState,
      [name]: Number(value.replace(/\D/g, '')),
    }))
  }

  const handleSaveAudience = (status) => async () => {
    setLoading((prev) => ({
      ...prev,
      save: true,
      ...(status === STATUSES.DRAFT ? { draft: true } : {}),
      ...(status === STATUSES.APPROVED ? { approved: true } : {}),
      ...(status === STATUSES.ARCHIVED ? { archive: true } : {}),
    }))
    try {
      if (pageType === AC_PAGE_TYPES.TX) {
        await updateTaxonomyAudience(id, { ...audience, status })
        history.push(`/taxonomies/${audience.taxonomy.slug}/audiences`)
      } else {
        await updateCustomAudience(id, {
          ...audience,
          ...(audience.expirationDateV2
            ? { expirationDateV2: new Date(audience.expirationDateV2) }
            : {}),
          status,
        })
        history.push(`/audience-creator`)
      }
      toast('Audience saved!')
    } catch (error) {
      logErrorAsync(error)
      toast.error(error.message)
    } finally {
      setLoading((prev) => ({
        ...prev,
        save: false,
        draft: false,
        approved: false,
        archive: false,
      }))
    }
  }

  const handleDeleteAudience = useCallback(async () => {
    setLoading((prev) => ({ ...prev, delete: true }))
    try {
      // TODO: add case to taxonomy audiences
      await deleteCustomAudience(id)
      history.push(`/audience-creator`)
      toast('Audience successfully deleted')
      setLoading((prev) => ({ ...prev, delete: false }))
    } catch (error) {
      logErrorAsync(error)
      toast.error(error.message)
      setLoading((prev) => ({ ...prev, delete: false }))
    }
  }, [])

  const handleViewChangelog = () => {
    const query = new URLSearchParams({
      collection: 'custom_audiences',
      id: audience._id,
      name: audience.name,
    })
    window.open(`/changelogs?${query.toString()}`, '_blank')
  }

  return (
    <div className={classNames('audience-creator')}>
      <If condition={audience}>
        <AudienceCreatorHeader
          onSaveDraft={handleSaveAudience(STATUSES.DRAFT)}
          onApprove={handleSaveAudience(STATUSES.APPROVED)}
          onArchive={handleSaveAudience(STATUSES.ARCHIVED)}
          onDelete={handleDeleteAudience}
          onChangelog={handleViewChangelog}
          status={audience?.status}
          hasChanges={hasChanges > 0}
          title={audience?.name}
          loading={loading}
          taxonomySlug={pageType === AC_PAGE_TYPES.TX ? audience.taxonomy.slug : null}
          extraActions={
            // a saved segment only - a new one is in no partner yet
            <If condition={pageType === AC_PAGE_TYPES.TX && audience._id}>
              <ApiIntegrations audience={audience} className="audience-creator-header__button" />
            </If>
          }
        />
      </If>
      <div className="audience-creator__content">
        <If condition={loading.audience}>
          <div className="audience-creator__loader-container">
            <Loader className="audience-creator__loader" />
          </div>
        </If>
        <If condition={audience && schema}>
          <div className="audience-creator__group border-b-0">
            <div className="audience-creator__group--header">
              <If condition={pageType === AC_PAGE_TYPES.AC}>Audience Creator</If>
              <If condition={pageType === AC_PAGE_TYPES.TX}>Taxonomy Audience</If>
              <div className="audience-creator__group--header-sizes">
                <IconACSizes /> ShareThis size: {format('metric', sizes?.sharethis)}
              </div>
            </div>
            <Section title="General" icon="general">
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 150px 150px', gap: 16 }}>
                <Input
                  label="Audience Name"
                  onChange={handleInputChange}
                  value={audience.name}
                  name="name"
                  disabled={disabledInputAndSelect || disabledAll}
                />
                <Choose>
                  <When condition={audience.geoAudience}>
                    <Input
                      label="Max Geo Audience Size"
                      name="geoAudienceSize"
                      onChange={handleInputNumberChange}
                      value={handleFormatNumber(audience?.geoAudienceSize)}
                      disabled={disabledInputAndSelect || disabledAll}
                    />
                  </When>
                  <Otherwise>
                    <Input
                      label="Preferred size"
                      name="preferredSize"
                      onChange={handleInputNumberChange}
                      value={handleFormatNumber(audience?.preferredSize)}
                      disabled={disabledInputAndSelect || disabledAll}
                    />
                  </Otherwise>
                </Choose>
                <Input
                  label="Partner TTL (in days)"
                  name="partnerTtl"
                  onChange={handleInputNumberChange}
                  value={handleFormatNumber(audience?.partnerTtl || 30)}
                />
                <Input
                  label="Expiration Date"
                  name="expirationDateV2"
                  type="date"
                  onChange={handleInputChange}
                  value={
                    audience.expirationDateV2
                      ? dayjs(audience.expirationDateV2).format('YYYY-MM-DD')
                      : null
                  }
                  min={dayjs().add(1, 'day').format('YYYY-MM-DD')}
                  max={dayjs().add(2, 'year').format('YYYY-MM-DD')}
                  disabled={disabledInputAndSelect || disabledAll}
                />
              </div>
              <If condition={pageType === AC_PAGE_TYPES.TX}>
                <SegmentIds segmentIds={audience.segmentIds} />
              </If>
              <GeneralFilters
                audience={audience}
                setAudience={setAudience}
                disabled={disabledAll}
                asyncData={{
                  regions: pageType === AC_PAGE_TYPES.AC ? regions : [],
                  languages: schema?.languages?.enum,
                }}
              />
              <If condition={pageType === AC_PAGE_TYPES.AC && regionSizes}>
                <RegionSizes regions={audience.regions} sizes={regionSizes} />
              </If>
              <If condition={(audience?.models ?? []).length}>
                <div className="audience-creator__custom-attributes">
                  <div className="audience-creator__custom-attributes-title">
                    Models
                  </div>
                  <div className="audience-creator__custom-attributes-filters">
                    <For each="attr" of={audience.models}>
                      <div
                        key={attr.name}
                      >
                        <span >{attr.name}</span>
                        <span >
                          top {Math.max(0, 100 - Number(attr?.percentile ?? 0))}%
                        </span>
                      </div>
                    </For>
                  </div>
                </div>
              </If>
            </Section>
            <If condition={pageType === AC_PAGE_TYPES.AC}>
              <Section title="Destinations" icon="destinations">
                <DestinationsFilter
                  audience={audience}
                  destinations={destinations}
                  disabled={disabledInputAndSelect || disabledAll}
                  setAudience={setAudience}
                />
                <SelectedDestinations
                  audience={audience}
                  disabled={disabledInputAndSelect || disabledAll}
                  schema={schema}
                  sizes={sizes}
                  onInputChange={handleInputChange}
                  setAudience={setAudience}
                />
              </Section>
            </If>
            <Section title="Signals" icon="signals">
              <SignalsFilter signals={signals} setSignals={setSignals} />
              <If condition={signals.Topics}>
                <SignalsBlock
                  audience={audience}
                  setAudience={setAudience}
                  handleInputChange={handleInputChange}
                  loading={loading}
                  setLoading={setLoading}
                  disabledAll={disabledAll}
                  nonSeedTopics={nonSeedTopics}
                  offlineAttributes={offlineAttributes}
                  restUseSelectedTopics={restUseSelectedTopics}
                />
              </If>
              <If condition={signals['URL Seeds']}>
                <SeedsBlock audience={audience} setAudience={setAudience} />
              </If>
              <If condition={signals['User ID Seeds']}>
                <UserIdSeedBlock audience={audience} setAudience={setAudience} />
              </If>
            </Section>
          </div>
        </If>
      </div>
    </div>
  )
}

export default React.memo(AudienceCreator)
